/**
 * Dashboard Layout Component
 * Shared layout for all dashboard pages with sidebar navigation
 */
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth, useLanguage, API } from "@/context/AppContext";
import {
  Home, Search, Briefcase, MessageSquare, CreditCard, Settings,
  Shield, Menu, X, LogOut, Building
} from "lucide-react";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { NotificationBell } from "@/components/NotificationBell";
import { RoleSwitcher } from "@/components/RoleSwitcher";

// Logo Component
const Logo = ({ className = "h-10 w-10", decorative = false }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png" 
    alt={decorative ? "" : "Infirmly Swiss Consulting logo"}
    aria-hidden={decorative ? "true" : undefined}
    className={`${className} rounded-full bg-white object-cover`}
    width="40"
    height="40"
    decoding="async"
  />
);

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelplyBanner, setShowHelplyBanner] = useState(true);

  const clientLinks = [
    { path: '/dashboard', icon: Home, label: t('dashboard') },
    { path: '/dashboard/jobs', icon: Briefcase, label: t('myRequests') || 'Meine Anfragen' },
    { path: '/categories', icon: Search, label: t('browseExperts') || 'Experten suchen' },
    { path: '/dashboard/messages', icon: MessageSquare, label: t('messages') },
    { path: '/dashboard/subscription', icon: CreditCard, label: t('subscription') },
    { path: '/dashboard/settings', icon: Settings, label: t('settings') },
  ];

  const providerLinks = [
    { path: '/dashboard', icon: Home, label: t('dashboard') },
    { path: '/dashboard/jobs', icon: Search, label: t('findJobsTitle') },
    { path: '/dashboard/bids', icon: Briefcase, label: t('myBids') },
    { path: '/dashboard/messages', icon: MessageSquare, label: t('messages') },
    { path: '/dashboard/subscription', icon: CreditCard, label: t('subscription') },
    { path: '/dashboard/settings', icon: Settings, label: t('settings') },
  ];

  // Admin users only see Dashboard, Messages, and Settings (no bids/subscription)
  const adminLinks = [
    { path: '/dashboard', icon: Home, label: t('dashboard') },
    { path: '/dashboard/messages', icon: MessageSquare, label: t('messages') },
    { path: '/dashboard/settings', icon: Settings, label: t('settings') },
  ];

  const links = user?.is_admin ? adminLinks : (user?.role === 'provider' ? providerLinks : clientLinks);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#050B23' }}>
      {/* Top Banner - Helply Swiss */}
      {showHelplyBanner && (
        <div className="bg-gradient-to-r from-red-600 via-purple-600 to-blue-600 text-white py-2 px-4 relative z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 text-sm">
            <Briefcase className="w-4 h-4" />
            <span>Handwerker-Services?</span>
            <a 
              href="https://helplyapp.gkv-consulting.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-bold underline hover:text-yellow-200"
            >
              Helply Swiss
            </a>
            <button 
              onClick={() => setShowHelplyBanner(false)}
              aria-label="Close Helply banner"
              className="absolute right-2 top-1/2 -translate-y-1/2 hover:bg-white/20 focus-visible:bg-white/20 rounded-full p-2 min-w-[32px] min-h-[32px] flex items-center justify-center"
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-1">
        {/* Mobile Header */}
        <header
          className="md:hidden fixed top-0 left-0 right-0 z-40 border-b border-white/10 h-16 flex items-center px-4"
          style={{ top: showHelplyBanner ? '32px' : '0', background: 'rgba(10,26,62,0.95)', backdropFilter: 'blur(12px)' }}
        >
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-white">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2 ml-4 flex-1">
            <Logo className="h-8 w-8" />
            <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
          </div>
          <div className="flex items-center gap-1 ml-auto" data-testid="mobile-top-actions">
            <RoleSwitcher />
            <NotificationBell />
          </div>
        </header>

        {/* Sidebar */}
        <aside
          className={`
            fixed left-0 z-30 h-full w-64 border-r border-white/10 transform transition-transform duration-200 flex flex-col
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
          `}
          style={{
            top: showHelplyBanner ? '32px' : '0',
            height: showHelplyBanner ? 'calc(100% - 32px)' : '100%',
            background: 'linear-gradient(180deg, #0A1A3E 0%, #050B23 100%)',
          }}
        >
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <Logo className="h-10 w-10" />
              <span className="font-bold text-xl text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === link.path 
                    ? 'bg-[#0044CC]/30 text-white font-medium border border-[#60a5fa]/30' 
                    : 'text-blue-100/70 hover:bg-white/5 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <link.icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{link.label}</span>
              </Link>
            ))}
            
            {/* Admin Link */}
            {user?.is_admin && (
              <Link
                to="/admin"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === '/admin' 
                    ? 'bg-purple-500/20 text-purple-200 font-medium border border-purple-400/40' 
                    : 'text-purple-300 hover:bg-purple-500/10 hover:text-purple-100'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5" strokeWidth={1.5} />
                <span>Admin</span>
              </Link>
            )}
          </nav>

          {/* User Profile & Logout - Fixed at bottom */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarImage src={user?.profile_picture} />
                <AvatarFallback className="bg-[#0044CC] text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate text-white">{user?.name}</p>
                <p className="text-xs text-blue-100/50 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-300 hover:bg-red-500/15 hover:text-red-200 rounded-lg mt-3 border border-red-400/30 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">{t('logout')}</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className="md:ml-64 flex-1 flex flex-col text-white infirmly-dark"
          style={{
            paddingTop: showHelplyBanner ? 'calc(32px + 64px)' : '64px',
            marginTop: showHelplyBanner ? '0' : '0',
            background: 'radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)',
            minHeight: '100vh',
          }}
        >
          <div className="flex-1 p-6 md:p-8 md:pt-6 pb-24 md:pb-8">
            {/* Desktop top action bar (mobile uses the header) */}
            <div className="hidden md:flex items-center justify-end gap-2 mb-4" data-testid="desktop-top-actions">
              <RoleSwitcher />
              <NotificationBell />
            </div>
            {children}
          </div>
          
          {/* Bottom Banner - GKV Consulting */}
          <div className="border-t border-white/10 py-3 px-4" style={{ background: 'rgba(5,11,35,0.6)' }}>
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
              <Building className="w-5 h-5 text-blue-200/60 hidden sm:block" />
              <div className="text-sm">
                <span className="text-blue-100/70">Ein Produkt von </span>
                <a 
                  href="https://gkv-consulting.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold text-white hover:text-[#60a5fa]"
                >
                  GKV-Consulting GmbH
                </a>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-white/20 bg-white/[0.05] text-white hover:bg-white/10 rounded-full text-xs"
                onClick={() => window.open('https://gkv-consulting.com/', '_blank')}
              >
                Mehr erfahren
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile-only bottom nav */}
      <MobileBottomNav language={typeof window !== 'undefined' ? localStorage.getItem('language') || 'en' : 'en'} />
    </div>
  );
};

export default DashboardLayout;
